import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../services/api';

// Customer Credit Balance — admin tab. Every admin-initiated credit is now
// issued as a Credit Memo (formal CM-XXXXX document + PDF + email). Memos and
// any pre-existing/programmatic credits render in one unified list by date.
//
// DEDUP RULE: a credit memo in 'specific'/'account_credit' mode generates a
// CustomerCredit linked back via the credit_memo_id field. Those credits are
// hidden from the list (filtered below) because the memo is already shown as
// its own row (with memo number + View PDF). 'reference_only' memos generate no
// credit. This keys on the memo LINK, not credit_type, so credits keep their
// real type (prepayment/broken_bag/…) while still deduping correctly.

interface CreditApplication {
  id: number;
  invoice_id: number;
  invoice_number: string;
  amount_applied: string;
  applied_at: string | null;
  applied_by: string | null;
}

interface CustomerCredit {
  id: number;
  amount: string;
  credit_type: string;
  credit_type_display: string;
  reference: string;
  notes: string;
  created_at: string | null;
  created_by: string | null;
  amount_applied: string;
  amount_remaining: string;
  credit_memo_id: number | null;
  applications: CreditApplication[];
}

interface CreditMemoLine {
  id: number;
  description: string;
  quantity: string;
  unit_price: string;
  line_total: string;
  sort_order: number;
}

interface CreditMemo {
  id: number;
  memo_number: string;
  memo_date: string | null;
  application_mode: 'specific' | 'account_credit' | 'reference_only';
  application_mode_display: string;
  reference_invoice_id: number | null;
  reference_invoice_number: string | null;
  target_invoice_id: number | null;
  target_invoice_number: string | null;
  subtotal: string;
  total_credit: string;
  notes: string;
  created_at: string | null;
  created_by: string | null;
  sent_at: string | null;
  customer_credit_id: number | null;
  amount_remaining: string | null;
  email_recipients: string[];
  can_email: boolean;
  qb_sync_status: 'pending' | 'synced' | 'failed';
  qb_credit_memo_id: string | null;
  qb_sync_error: string | null;
  qb_last_sync_at: string | null;
  lines: CreditMemoLine[];
  applications: CreditApplication[];
}

interface SlimInvoice {
  id: number;
  invoice_number: string;
  status: string;
  balance_due: string;
  due_date: string | null;
  is_billable: boolean;
}

interface PortalUser {
  id: number;
  email: string;
  name: string;
  role: string;
}

// One catalog picker row — a single priced form/packaging variant of a feed.
interface AvailableFeed {
  id: string;            // synthetic, e.g. "formula-5-bag-mash"
  formula_id: number;
  description: string;   // "Layer Mash — 40lb bag, Mash"
  unit_price: string;
  price_unit: string;    // "bag" | "lb"
  form: string;
  packaging: string;
}

// One line item from a reference invoice (via /invoices/<id>/full/).
interface InvoiceLine {
  id: number;
  description: string;
  unit_price: string;
  unit: string;          // "bags" | "lbs" | "sacks" | "each"
  quantity: number;
}

interface MemosResponse {
  customer_id: number;
  customer_name: string;
  credit_balance: string;
  credit_memos: CreditMemo[];
  credits: CustomerCredit[];
  invoices: SlimInvoice[];
  recipients: string[];
  recipients_source: 'credit_memo_recipients' | 'default_invoice_recipients' | 'customer_email';
  portal_users: PortalUser[];
  available_feeds: AvailableFeed[];
}

interface CustomerCreditsTabProps {
  customerId: number;
  customerName: string;
}

interface MemoLineInput {
  id: string;
  selection: string;     // '' | 'custom' | 'inv:<id>' | 'feed:<synthetic-id>'
  is_custom: boolean;    // true only when 'Custom' picked → description/price editable
  description: string;
  quantity: string;
  unit_price: string;
}

const CREDIT_TYPE_OPTIONS = [
  { key: 'prepayment', name: 'Customer Prepayment' },
  { key: 'broken_bag', name: 'Broken Bag Credit' },
  { key: 'refund', name: 'Refund' },
  { key: 'manual_adjustment', name: 'Manual Adjustment' },
  { key: 'correction', name: 'Correction (offsetting entry)' },
];

const APPLICATION_MODES = [
  {
    key: 'specific',
    label: 'Apply to specific invoice',
    help: 'Reduces the balance of one specific invoice. Use when the customer wants the credit attached to a particular open invoice.',
  },
  {
    key: 'account_credit',
    label: 'Apply as account credit (FIFO, oldest first)',
    help: "Creates an account-level credit. Reduces the customer's balance on their oldest unpaid invoice first. Use when the customer needs the credit applied to their account but not tied to a specific invoice (matches Kevin/Snohomish-style accounting).",
  },
  {
    key: 'reference_only',
    label: 'Reference only — no balance impact',
    help: "Generates the credit memo PDF for the customer's records but does not affect their account balance. Use when the customer has already received credit through another mechanism and just needs the documentation.",
  },
] as const;

const isValidEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
const sameMembers = (a: Set<string>, b: Set<string>) =>
  a.size === b.size && Array.from(a).every((x) => b.has(x));

const RECIPIENT_SOURCE_LABEL: Record<string, string> = {
  credit_memo_recipients: 'credit memo recipients',
  default_invoice_recipients: 'invoice recipients',
  customer_email: 'customer email',
};

const fmtMoney = (v: string | number) => {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const fmtDate = (d: string | null) =>
  d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

const todayISO = () => new Date().toISOString().slice(0, 10);

// Clamp an auto-filled price to 2dp so the modal's live total matches what the
// backend stores (CreditMemoLine.unit_price is decimal 2dp). Bulk invoice lines
// carry a 4dp per-lb rate; this rounds to cents.
const round2 = (v: string | number) => {
  const n = parseFloat(String(v));
  return Number.isNaN(n) ? '' : n.toFixed(2);
};

const newLine = (): MemoLineInput => ({
  id: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
  selection: '', is_custom: false, description: '', quantity: '1', unit_price: '',
});

// Per-row QuickBooks sync badge. 'pending' shows only once a sync has been
// attempted-and-not-yet-resolved is irrelevant here — pending means queued
// (e.g. QB not configured, or awaiting the on_commit push).
const QbSyncBadge: React.FC<{ memo: CreditMemo }> = ({ memo }) => {
  const map: Record<string, { label: string; bg: string; fg: string }> = {
    synced: { label: 'QB ✓ Synced', bg: '#d1fae5', fg: '#065f46' },
    pending: { label: 'QB ⏳ Pending', bg: '#f3f4f6', fg: '#374151' },
    failed: { label: 'QB ⚠ Failed', bg: '#fee2e2', fg: '#991b1b' },
  };
  const s = map[memo.qb_sync_status] || map.pending;
  return (
    <span
      data-testid={`qb-badge-${memo.id}`}
      title={memo.qb_sync_status === 'failed' ? (memo.qb_sync_error || 'QB sync failed') : undefined}
      style={{ background: s.bg, color: s.fg, fontSize: 11, fontWeight: 600, padding: '1px 7px', borderRadius: 10, whiteSpace: 'nowrap' }}
    >
      {s.label}
    </span>
  );
};

const CustomerCreditsTab: React.FC<CustomerCreditsTabProps> = ({ customerId, customerName }) => {
  const [credits, setCredits] = useState<CustomerCredit[]>([]);
  const [memos, setMemos] = useState<CreditMemo[]>([]);
  const [invoices, setInvoices] = useState<SlimInvoice[]>([]);
  const [recipients, setRecipients] = useState<string[]>([]);
  const [recipientsSource, setRecipientsSource] = useState<string>('customer_email');
  const [portalUsers, setPortalUsers] = useState<PortalUser[]>([]);
  const [availableFeeds, setAvailableFeeds] = useState<AvailableFeed[]>([]);
  // Lazy-loaded line items from the chosen reference invoice (the "From this
  // invoice" picker section). Empty until a reference invoice is selected.
  const [invoiceLines, setInvoiceLines] = useState<InvoiceLine[]>([]);
  const [balance, setBalance] = useState<string>('0');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // Per-row resend / QB-retry tracking + transient banner feedback.
  const [resendingId, setResendingId] = useState<number | null>(null);
  const [retryingQbId, setRetryingQbId] = useState<number | null>(null);
  const [memoMessage, setMemoMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Create Credit Memo modal state
  const [showMemoForm, setShowMemoForm] = useState(false);
  const [memoDate, setMemoDate] = useState(todayISO());
  const [memoCreditType, setMemoCreditType] = useState('');
  const [appMode, setAppMode] = useState<'specific' | 'account_credit' | 'reference_only'>('account_credit');
  const [refInvoiceId, setRefInvoiceId] = useState('');
  const [targetInvoiceId, setTargetInvoiceId] = useState('');
  const [memoNotes, setMemoNotes] = useState('');
  const [memoLines, setMemoLines] = useState<MemoLineInput[]>([newLine()]);
  const [memoSubmitting, setMemoSubmitting] = useState(false);
  const [memoError, setMemoError] = useState('');
  // Recipient picker: selectedRecipients is the single source of truth for the
  // send-to set; additionalEmails are admin-typed one-offs (always selected).
  const [selectedRecipients, setSelectedRecipients] = useState<Set<string>>(new Set());
  const [additionalEmails, setAdditionalEmails] = useState<string[]>([]);
  const [additionalEmail, setAdditionalEmail] = useState('');
  const [additionalError, setAdditionalError] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await fetch(`${API_BASE_URL}/admin/customers/${customerId}/credit-memos/`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to load credits');
      const data: MemosResponse = await res.json();
      setCredits(data.credits || []);
      setMemos(data.credit_memos || []);
      setInvoices(data.invoices || []);
      setRecipients(data.recipients || []);
      setRecipientsSource(data.recipients_source || 'customer_email');
      setPortalUsers(data.portal_users || []);
      setAvailableFeeds(data.available_feeds || []);
      setBalance(data.credit_balance || '0');
    } catch (err: any) {
      console.error('Error loading credits:', err);
      setError(err.message || 'Failed to load credits');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Credit Memo modal ──
  const resetMemoForm = () => {
    setMemoDate(todayISO());
    setMemoCreditType('');
    setAppMode('account_credit');
    setRefInvoiceId('');
    setInvoiceLines([]);
    setTargetInvoiceId('');
    setMemoNotes('');
    setMemoLines([newLine()]);
    setMemoError('');
    // Default the picker to the resolved fallback recipients.
    setSelectedRecipients(new Set(recipients));
    setAdditionalEmails([]);
    setAdditionalEmail('');
    setAdditionalError('');
  };

  const toggleRecipient = (email: string) => {
    setSelectedRecipients((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };
  const addAdditionalEmail = () => {
    const e = additionalEmail.trim();
    if (!e) return;
    if (!isValidEmail(e)) { setAdditionalError('Enter a valid email address.'); return; }
    setAdditionalEmails((prev) => (prev.includes(e) ? prev : [...prev, e]));
    setSelectedRecipients((prev) => new Set(prev).add(e));
    setAdditionalEmail('');
    setAdditionalError('');
  };
  const removeAdditionalEmail = (email: string) => {
    setAdditionalEmails((prev) => prev.filter((x) => x !== email));
    setSelectedRecipients((prev) => {
      const next = new Set(prev);
      next.delete(email);
      return next;
    });
  };

  const updateLine = (id: string, field: keyof MemoLineInput, value: string) => {
    setMemoLines((prev) => prev.map((l) => (l.id === id ? { ...l, [field]: value } : l)));
  };
  const addLine = () => setMemoLines((prev) => [...prev, newLine()]);
  const removeLine = (id: string) => {
    setMemoLines((prev) => (prev.length === 1 ? prev : prev.filter((l) => l.id !== id)));
  };

  // Reference invoice drives both the QB linkage AND the "From this invoice"
  // picker section. Lazy-fetch that invoice's line items when it changes.
  const handleRefInvoiceChange = async (value: string) => {
    setRefInvoiceId(value);
    if (!value) { setInvoiceLines([]); return; }
    try {
      const res = await fetch(`${API_BASE_URL}/invoices/${value}/full/`, { credentials: 'include' });
      if (!res.ok) { setInvoiceLines([]); return; }
      const data = await res.json();
      const items: InvoiceLine[] = (data.items || []).map((it: any) => ({
        id: it.id,
        description: it.description_display || it.feed_name || 'Line item',
        unit_price: String(it.unit_price ?? '0'),
        unit: it.unit || 'each',
        quantity: Number(it.quantity ?? 1),
      }));
      setInvoiceLines(items);
    } catch {
      setInvoiceLines([]);
    }
  };

  // Picker selection → auto-fill the line. Catalog + invoice picks are
  // authoritative (description/price read-only); 'custom' opens freeform.
  // unit_price is rounded to 2dp to match CreditMemoLine's column + the live total.
  const handleProductPick = (lineId: string, value: string) => {
    setMemoLines((prev) => prev.map((l) => {
      if (l.id !== lineId) return l;
      if (value === '') {
        return { ...l, selection: '', is_custom: false, description: '', unit_price: '' };
      }
      if (value === 'custom') {
        return { ...l, selection: 'custom', is_custom: true, description: '', unit_price: '' };
      }
      if (value.startsWith('inv:')) {
        const inv = invoiceLines.find((x) => `inv:${x.id}` === value);
        if (!inv) return { ...l, selection: value };
        return {
          ...l, selection: value, is_custom: false,
          description: inv.description,
          unit_price: round2(inv.unit_price),
          quantity: String(inv.quantity || l.quantity || '1'),
        };
      }
      // feed:<synthetic-id>
      const feed = availableFeeds.find((f) => `feed:${f.id}` === value);
      if (!feed) return { ...l, selection: value };
      return {
        ...l, selection: value, is_custom: false,
        description: feed.description,
        unit_price: round2(feed.unit_price),
      };
    }));
  };

  const lineTotal = (l: MemoLineInput) => {
    const q = parseFloat(l.quantity);
    const u = parseFloat(l.unit_price);
    if (Number.isNaN(q) || Number.isNaN(u)) return 0;
    return Math.round(q * u * 100) / 100;
  };
  const memoTotal = memoLines.reduce((sum, l) => sum + lineTotal(l), 0);

  const handleMemoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMemoError('');
    // Client validation (the endpoint re-validates).
    if (!memoCreditType) { setMemoError('Choose a credit type.'); return; }
    for (let i = 0; i < memoLines.length; i++) {
      const l = memoLines[i];
      if (!l.description.trim()) { setMemoError(`Line ${i + 1}: description is required.`); return; }
      const q = parseFloat(l.quantity);
      const u = parseFloat(l.unit_price);
      if (Number.isNaN(q) || q <= 0) { setMemoError(`Line ${i + 1}: quantity must be greater than 0.`); return; }
      if (Number.isNaN(u) || u < 0) { setMemoError(`Line ${i + 1}: unit price must be 0 or more.`); return; }
    }
    if (memoTotal <= 0) { setMemoError('Total credit must be greater than $0.'); return; }
    if (appMode === 'specific' && !targetInvoiceId) {
      setMemoError('Choose a target invoice for specific mode.');
      return;
    }

    setMemoSubmitting(true);
    try {
      // If the selection exactly matches the resolved defaults, send [] so the
      // backend stores empty and Commit 5 re-resolves dynamically at send time.
      // Any divergence pins the audience to the explicit list.
      const matchesDefaults = sameMembers(selectedRecipients, new Set(recipients));
      const recipientsOverride = matchesDefaults ? [] : Array.from(selectedRecipients);

      const body: Record<string, unknown> = {
        memo_date: memoDate,
        credit_type: memoCreditType,
        application_mode: appMode,
        reference_invoice_id: refInvoiceId ? Number(refInvoiceId) : null,
        notes: memoNotes.trim(),
        recipients_override: recipientsOverride,
        lines: memoLines.map((l) => ({
          description: l.description.trim(),
          quantity: parseFloat(l.quantity),
          unit_price: parseFloat(l.unit_price),
        })),
      };
      if (appMode === 'specific') body.target_invoice_id = Number(targetInvoiceId);

      const res = await fetch(`${API_BASE_URL}/admin/customers/${customerId}/credit-memos/`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setMemoError(data.error || 'Failed to create credit memo.');
        return; // keep modal open
      }
      setShowMemoForm(false);
      resetMemoForm();
      await loadData();
    } catch (err: any) {
      setMemoError(err.message || 'Failed to create credit memo.');
    } finally {
      setMemoSubmitting(false);
    }
  };

  const viewMemoPdf = (memo: CreditMemo) => {
    window.open(`${API_BASE_URL}/admin/credit-memos/${memo.id}/pdf/`, '_blank', 'noopener');
  };

  const resendMemo = async (memo: CreditMemo) => {
    setMemoMessage(null);
    setResendingId(memo.id);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/credit-memos/${memo.id}/resend/`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMemoMessage({ type: 'error', text: data.error || 'Failed to resend credit memo.' });
        return;
      }
      const sent = (data.sent || []).length;
      const failed = (data.failed || []).length;
      setMemoMessage(
        failed > 0
          ? { type: 'error', text: `Sent to ${sent}, failed for ${failed} recipient(s).` }
          : { type: 'success', text: `Credit memo ${memo.memo_number} sent to ${sent} recipient(s).` }
      );
      await loadData();
    } catch (err: any) {
      setMemoMessage({ type: 'error', text: err.message || 'Failed to resend credit memo.' });
    } finally {
      setResendingId(null);
    }
  };

  const retryQbSync = async (memo: CreditMemo) => {
    setMemoMessage(null);
    setRetryingQbId(memo.id);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/credit-memos/${memo.id}/retry-qb-sync/`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMemoMessage({ type: 'error', text: data.error || 'Failed to retry QB sync.' });
        return;
      }
      setMemoMessage(
        data.qb_sync_status === 'synced'
          ? { type: 'success', text: `Credit memo ${memo.memo_number} synced to QuickBooks.` }
          : { type: 'error', text: `QB sync still failing: ${data.qb_sync_error || 'unknown error'}` }
      );
      await loadData();
    } catch (err: any) {
      setMemoMessage({ type: 'error', text: err.message || 'Failed to retry QB sync.' });
    } finally {
      setRetryingQbId(null);
    }
  };

  const toggleExpand = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const balanceNum = parseFloat(balance) || 0;

  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: 14, fontWeight: 500, color: '#374151', marginBottom: 4,
  };
  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 12px', fontSize: 16, border: '1px solid #d1d5db',
    borderRadius: 8, boxSizing: 'border-box',
  };
  const btnPrimary: React.CSSProperties = {
    background: '#059669', color: '#fff', fontWeight: 600, padding: '10px 16px',
    borderRadius: 8, border: 'none', minHeight: 44, cursor: 'pointer',
  };
  const btnSecondary: React.CSSProperties = {
    background: '#fff', color: '#374151', fontWeight: 500, padding: '10px 16px',
    borderRadius: 8, border: '1px solid #d1d5db', minHeight: 44, cursor: 'pointer',
  };

  if (loading && credits.length === 0 && memos.length === 0) {
    return <div style={{ padding: 24, color: '#6b7280' }}>Loading credits…</div>;
  }

  // Unified, date-sorted rows. See DEDUP RULE at the top of the file: credits
  // generated by a memo (credit_memo_id set) are hidden — they're shown as
  // their memo row instead.
  const visibleCredits = credits.filter((c) => !c.credit_memo_id);
  type Row =
    | { kind: 'memo'; date: string; memo: CreditMemo }
    | { kind: 'credit'; date: string; credit: CustomerCredit };
  const rows: Row[] = [
    ...memos.map((m) => ({ kind: 'memo' as const, date: m.created_at || '', memo: m })),
    ...visibleCredits.map((c) => ({ kind: 'credit' as const, date: c.created_at || '', credit: c })),
  ].sort((a, b) => (b.date || '').localeCompare(a.date || ''));

  // Resolved recipients that aren't portal users (e.g. the customer email
  // fallback) get their own checkbox, mirroring the invoice picker's primary
  // fallback. Admin-typed additional emails render as their own removable rows.
  const extraResolved = recipients.filter(
    (e) => !portalUsers.some((u) => u.email === e) && !additionalEmails.includes(e),
  );
  const sendToList = Array.from(selectedRecipients);

  return (
    <div style={{ padding: 16 }}>
      {/* Balance card */}
      <div
        style={{
          background: balanceNum > 0 ? '#ecfdf5' : '#f9fafb',
          border: `1px solid ${balanceNum > 0 ? '#a7f3d0' : '#e5e7eb'}`,
          borderRadius: 12, padding: 20, marginBottom: 16,
          display: 'flex', flexWrap: 'wrap', gap: 12,
          alignItems: 'center', justifyContent: 'space-between',
        }}
      >
        <div>
          <div style={{ fontSize: 13, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Credit Balance
          </div>
          <div style={{ fontSize: 32, fontWeight: 700, color: balanceNum > 0 ? '#059669' : '#111827' }}>
            {fmtMoney(balance)}
          </div>
          <div style={{ fontSize: 13, color: '#6b7280' }}>available for {customerName}</div>
        </div>
        <button style={btnPrimary} onClick={() => { resetMemoForm(); setShowMemoForm(true); }}>
          + Create Credit Memo
        </button>
      </div>

      {error && (
        <div style={{ background: '#fef2f2', color: '#b91c1c', padding: 12, borderRadius: 8, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {memoMessage && (
        <div
          role="alert"
          style={{
            background: memoMessage.type === 'success' ? '#ecfdf5' : '#fef2f2',
            color: memoMessage.type === 'success' ? '#065f46' : '#b91c1c',
            padding: 12, borderRadius: 8, marginBottom: 16, fontSize: 14,
          }}
        >
          {memoMessage.text}
        </div>
      )}

      {/* Create Credit Memo modal */}
      {showMemoForm && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
          }}
          onClick={() => !memoSubmitting && setShowMemoForm(false)}
        >
          <form
            onSubmit={handleMemoSubmit}
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 12, padding: 24, width: '100%', maxWidth: 640,
              maxHeight: '90vh', overflowY: 'auto',
            }}
          >
            <h3 style={{ margin: '0 0 16px', fontSize: 18, color: '#111827' }}>Create Credit Memo — {customerName}</h3>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
              <div style={{ flex: '1 1 180px' }}>
                <label style={labelStyle} htmlFor="cm-date">Memo Date *</label>
                <input id="cm-date" type="date" style={inputStyle} value={memoDate} onChange={(e) => setMemoDate(e.target.value)} required />
              </div>
              <div style={{ flex: '1 1 220px' }}>
                <label style={labelStyle} htmlFor="cm-credit-type">Credit Type *</label>
                <select id="cm-credit-type" style={inputStyle} value={memoCreditType} onChange={(e) => setMemoCreditType(e.target.value)} required>
                  <option value="">— Choose a reason —</option>
                  {CREDIT_TYPE_OPTIONS.map((o) => (
                    <option key={o.key} value={o.key}>{o.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle} htmlFor="cm-ref">Reference Invoice</label>
              <select id="cm-ref" style={inputStyle} value={refInvoiceId} onChange={(e) => handleRefInvoiceChange(e.target.value)}>
                <option value="">— None —</option>
                {invoices.map((i) => (
                  <option key={i.id} value={i.id}>{i.invoice_number} ({i.status})</option>
                ))}
              </select>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                Setting a reference invoice lets you pick its exact line items (with the prices the customer paid) in the picker below.
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Application Mode *</label>
              <div role="radiogroup" aria-label="Application mode">
                {APPLICATION_MODES.map((m) => (
                  <div key={m.key} style={{ padding: '4px 0' }}>
                    <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14, color: '#374151', cursor: 'pointer' }}>
                      <input
                        type="radio" name="cm-mode" value={m.key}
                        checked={appMode === m.key}
                        onChange={() => setAppMode(m.key as typeof appMode)}
                      />
                      <span>{m.label}</span>
                    </label>
                    <div style={{ marginLeft: 24, marginTop: 2, fontSize: 12, color: '#6b7280', lineHeight: 1.4 }}>
                      {m.help}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {appMode === 'specific' && (
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle} htmlFor="cm-target">Target Invoice *</label>
                <select id="cm-target" style={inputStyle} value={targetInvoiceId} onChange={(e) => setTargetInvoiceId(e.target.value)}>
                  <option value="">— Choose an invoice —</option>
                  {invoices.filter((i) => i.is_billable).map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.invoice_number} · {fmtMoney(i.balance_due)} due ({i.status})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Line items — pick from this invoice / catalog, or enter a custom one */}
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Line Items *</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {memoLines.map((l, idx) => {
                  const readOnly = !l.is_custom;
                  const roStyle: React.CSSProperties = readOnly
                    ? { background: '#f9fafb', color: '#374151', cursor: 'not-allowed' }
                    : {};
                  return (
                    <div key={l.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <select
                        aria-label={`Line ${idx + 1} product`}
                        style={{ ...inputStyle, fontSize: 14, minHeight: 44 }}
                        value={l.selection}
                        onChange={(e) => handleProductPick(l.id, e.target.value)}
                      >
                        <option value="">— Select a product —</option>
                        {invoiceLines.length > 0 && (
                          <optgroup label="From this invoice">
                            {invoiceLines.map((line) => (
                              <option key={`inv-${line.id}`} value={`inv:${line.id}`}>
                                {line.description} — ${line.unit_price}/{line.unit} ({line.quantity} invoiced)
                              </option>
                            ))}
                          </optgroup>
                        )}
                        {availableFeeds.length > 0 && (
                          <optgroup label="Available products">
                            {availableFeeds.map((feed) => (
                              <option key={feed.id} value={`feed:${feed.id}`}>
                                {feed.description} — ${feed.unit_price}/{feed.price_unit}
                              </option>
                            ))}
                          </optgroup>
                        )}
                        <optgroup label="Custom">
                          <option value="custom">+ Custom description (one-off credit)</option>
                        </optgroup>
                      </select>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                        <input
                          aria-label={`Line ${idx + 1} description`} placeholder={l.selection ? 'Description' : 'Select a product'}
                          style={{ ...inputStyle, flex: '2 1 200px', fontSize: 14, ...roStyle }}
                          value={l.description} readOnly={readOnly}
                          onChange={(e) => updateLine(l.id, 'description', e.target.value)}
                        />
                        <input
                          aria-label={`Line ${idx + 1} quantity`} type="number" step="0.01" min="0" placeholder="Qty"
                          style={{ ...inputStyle, flex: '0 1 80px', fontSize: 14 }}
                          value={l.quantity} onChange={(e) => updateLine(l.id, 'quantity', e.target.value)}
                        />
                        <input
                          aria-label={`Line ${idx + 1} unit price`} type="number" step="0.01" min="0" placeholder="Unit $"
                          style={{ ...inputStyle, flex: '0 1 100px', fontSize: 14, ...roStyle }}
                          value={l.unit_price} readOnly={readOnly}
                          onChange={(e) => updateLine(l.id, 'unit_price', e.target.value)}
                        />
                        <span style={{ flex: '0 1 90px', textAlign: 'right', fontWeight: 600, color: '#111827' }} data-testid={`line-total-${idx}`}>
                          {fmtMoney(lineTotal(l))}
                        </span>
                        <button
                          type="button" aria-label={`Remove line ${idx + 1}`}
                          onClick={() => removeLine(l.id)} disabled={memoLines.length === 1}
                          style={{ background: 'none', border: 'none', color: memoLines.length === 1 ? '#d1d5db' : '#b91c1c', cursor: memoLines.length === 1 ? 'not-allowed' : 'pointer', fontSize: 18, padding: '0 4px' }}
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <button
                type="button" onClick={addLine}
                style={{ background: 'none', border: 'none', color: '#059669', cursor: 'pointer', padding: '8px 0 0', fontSize: 13 }}
              >
                + Add Line
              </button>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16, marginBottom: 14, fontSize: 15 }}>
              <span style={{ color: '#6b7280' }}>Total Credit:</span>
              <span style={{ fontWeight: 700, color: '#111827' }} data-testid="memo-total">{fmtMoney(memoTotal)}</span>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle} htmlFor="cm-notes">Notes</label>
              <textarea
                id="cm-notes" rows={2} style={{ ...inputStyle, resize: 'vertical' }}
                value={memoNotes} onChange={(e) => setMemoNotes(e.target.value)}
                placeholder="e.g. Credit issued for 2 damaged bags from recent delivery."
              />
            </div>

            <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 14 }}>
              <div style={{ ...labelStyle, marginBottom: 6 }}>Email Recipients</div>
              <div style={{ color: '#6b7280', fontSize: 12, marginBottom: 8 }}>
                Defaults to {RECIPIENT_SOURCE_LABEL[recipientsSource] || recipientsSource}. Adjust who receives this memo.
              </div>

              {portalUsers.map((u) => (
                <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 13, color: '#374151', cursor: 'pointer' }}>
                  <input type="checkbox" checked={selectedRecipients.has(u.email)} onChange={() => toggleRecipient(u.email)} />
                  <span>{u.email}</span>
                  <span style={{ color: '#6b7280', fontSize: 12 }}>
                    ({u.name !== u.email ? `${u.name} — ` : ''}{u.role.charAt(0).toUpperCase() + u.role.slice(1)})
                  </span>
                </label>
              ))}

              {extraResolved.map((email) => (
                <label key={email} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 13, color: '#374151', cursor: 'pointer' }}>
                  <input type="checkbox" checked={selectedRecipients.has(email)} onChange={() => toggleRecipient(email)} />
                  <span>{email}</span>
                  <span style={{ color: '#6b7280', fontSize: 12 }}>(on file)</span>
                </label>
              ))}

              {additionalEmails.map((email) => (
                <div key={email} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 13, color: '#374151' }}>
                  <input type="checkbox" checked readOnly />
                  <span>{email}</span>
                  <span style={{ color: '#6b7280', fontSize: 12 }}>(added)</span>
                  <button type="button" aria-label={`Remove ${email}`} onClick={() => removeAdditionalEmail(email)}
                    style={{ background: 'none', border: 'none', color: '#b91c1c', cursor: 'pointer', fontSize: 16, padding: '0 4px' }}>
                    ×
                  </button>
                </div>
              ))}

              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 8 }}>
                <input
                  type="email" aria-label="Additional recipient email" placeholder="Send to additional address"
                  style={{ ...inputStyle, fontSize: 13, flex: 1 }}
                  value={additionalEmail}
                  onChange={(e) => { setAdditionalEmail(e.target.value); setAdditionalError(''); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAdditionalEmail(); } }}
                />
                <button type="button" onClick={addAdditionalEmail} style={{ ...btnSecondary, minHeight: 'auto', padding: '8px 12px', fontSize: 13 }}>
                  Add
                </button>
              </div>
              {additionalError && <div style={{ color: '#b91c1c', fontSize: 12, marginTop: 4 }}>{additionalError}</div>}

              <div style={{ marginTop: 10, fontSize: 12, color: sendToList.length === 0 ? '#b91c1c' : '#374151' }} data-testid="send-to-summary">
                {sendToList.length === 0
                  ? 'No recipients selected — this memo will not be emailed.'
                  : <>Send to: <strong>{sendToList.join(', ')}</strong></>}
              </div>
              <div style={{ color: '#9ca3af', marginTop: 6, fontSize: 12 }}>
                The memo PDF is emailed to these recipients automatically when you create it. You can resend it later from the list.
              </div>
            </div>

            {memoError && (
              <div role="alert" style={{ background: '#fef2f2', color: '#b91c1c', padding: 10, borderRadius: 8, marginBottom: 14, fontSize: 13 }}>
                {memoError}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button type="button" style={btnSecondary} onClick={() => { setShowMemoForm(false); resetMemoForm(); }} disabled={memoSubmitting}>
                Cancel
              </button>
              <button type="submit" style={btnPrimary} disabled={memoSubmitting || !memoCreditType}>
                {memoSubmitting ? 'Creating…' : 'Create Credit Memo'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Unified history */}
      {rows.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>
          No credits or credit memos yet. Use “Add Credit” for a prepayment/refund, or “Create Credit Memo” to issue a formal document.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.map((row) => {
            if (row.kind === 'memo') {
              const m = row.memo;
              const key = `m-${m.id}`;
              const isOpen = expanded.has(key);
              return (
                <div key={key} style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', background: '#fffdf7' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', justifyContent: 'space-between', padding: 14 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600, color: '#111827' }}>
                        {fmtMoney(m.total_credit)}{' '}
                        <span style={{ fontWeight: 400, color: '#6b7280', fontSize: 13 }}>· {m.application_mode_display}</span>
                      </div>
                      <div style={{ fontSize: 12, color: '#6b7280', display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                        <span>
                          <strong>{m.memo_number}</strong> · {fmtDate(m.memo_date)}
                          {m.reference_invoice_number ? ` · ref ${m.reference_invoice_number}` : ''}
                          {m.created_by ? ` · ${m.created_by}` : ''}
                          {m.sent_at ? ` · Sent ${fmtDate(m.sent_at)}` : ''}
                        </span>
                        <QbSyncBadge memo={m} />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                      <button type="button" onClick={() => viewMemoPdf(m)} style={{ ...btnSecondary, minHeight: 'auto', padding: '6px 12px', fontSize: 13 }}>
                        View PDF
                      </button>
                      <button
                        type="button"
                        onClick={() => resendMemo(m)}
                        disabled={!m.can_email || resendingId === m.id}
                        title={m.can_email ? 'Email this memo to its recipients' : 'No recipients configured for this memo'}
                        style={{ ...btnSecondary, minHeight: 'auto', padding: '6px 12px', fontSize: 13, opacity: (!m.can_email || resendingId === m.id) ? 0.5 : 1, cursor: (!m.can_email || resendingId === m.id) ? 'not-allowed' : 'pointer' }}
                      >
                        {resendingId === m.id ? 'Sending…' : (m.sent_at ? 'Resend' : 'Send')}
                      </button>
                      {m.qb_sync_status === 'failed' && (
                        <button
                          type="button"
                          onClick={() => retryQbSync(m)}
                          disabled={retryingQbId === m.id}
                          title={m.qb_sync_error || 'Retry the QuickBooks sync'}
                          style={{ ...btnSecondary, minHeight: 'auto', padding: '6px 12px', fontSize: 13, opacity: retryingQbId === m.id ? 0.5 : 1, cursor: retryingQbId === m.id ? 'not-allowed' : 'pointer' }}
                        >
                          {retryingQbId === m.id ? 'Syncing…' : 'Retry QB Sync'}
                        </button>
                      )}
                      {m.applications.length > 0 && (
                        <button type="button" onClick={() => toggleExpand(key)} style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: 13, cursor: 'pointer' }}>
                          {isOpen ? '▾' : '▸'} {m.applications.length}
                        </button>
                      )}
                    </div>
                  </div>
                  {isOpen && m.applications.length > 0 && (
                    <div style={{ background: '#f9fafb', borderTop: '1px solid #e5e7eb', padding: 12 }}>
                      {m.applications.map((a) => (
                        <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6, padding: '6px 0', fontSize: 13, color: '#374151' }}>
                          <span>{a.invoice_number}</span>
                          <span style={{ color: '#6b7280' }}>{fmtDate(a.applied_at)}</span>
                          <span style={{ fontWeight: 600 }}>{fmtMoney(a.amount_applied)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            const c = row.credit;
            const key = `c-${c.id}`;
            const isOpen = expanded.has(key);
            const negative = parseFloat(c.amount) < 0;
            return (
              <div key={key} style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
                <div
                  style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', justifyContent: 'space-between', padding: 14, cursor: c.applications.length ? 'pointer' : 'default', background: '#fff' }}
                  onClick={() => c.applications.length && toggleExpand(key)}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: negative ? '#b91c1c' : '#111827' }}>
                      {fmtMoney(c.amount)} <span style={{ fontWeight: 400, color: '#6b7280', fontSize: 13 }}>· {c.credit_type_display}</span>
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>
                      {fmtDate(c.created_at)}{c.created_by ? ` · ${c.created_by}` : ''}{c.reference ? ` · ${c.reference}` : ''}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>Remaining</div>
                      <div style={{ fontWeight: 600, color: '#111827' }}>{fmtMoney(c.amount_remaining)}</div>
                    </div>
                    {c.applications.length > 0 && (
                      <span style={{ color: '#6b7280', fontSize: 13 }}>{isOpen ? '▾' : '▸'} {c.applications.length}</span>
                    )}
                  </div>
                </div>

                {isOpen && c.applications.length > 0 && (
                  <div style={{ background: '#f9fafb', borderTop: '1px solid #e5e7eb', padding: 12 }}>
                    {c.applications.map((a) => (
                      <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6, padding: '6px 0', fontSize: 13, color: '#374151' }}>
                        <span>{a.invoice_number}</span>
                        <span style={{ color: '#6b7280' }}>{fmtDate(a.applied_at)}</span>
                        <span style={{ fontWeight: 600 }}>{fmtMoney(a.amount_applied)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CustomerCreditsTab;
